import {
  type EditorPartitionSnapshot,
  type EditorPartitionTab,
  surfaceInstanceId,
} from '@hibro/shared';

/** Identity of one open editor instance tab. */
function tabKey(tab: EditorPartitionTab): string {
  return `${tab.viewId}\0${tab.instanceKey}`;
}

/**
 * Which instance tabs to close and which to open so the strip matches `next`.
 * Order of `open` follows `next`. Tabs already open stay where they are.
 */
export function diffEditorPartitionTabs(
  current: readonly EditorPartitionTab[],
  next: readonly EditorPartitionTab[],
): { close: EditorPartitionTab[]; open: EditorPartitionTab[] } {
  const nextKeys = new Set(next.map(tabKey));
  const currentKeys = new Set(current.map(tabKey));
  return {
    close: current.filter((tab) => !nextKeys.has(tabKey(tab))),
    open: next.filter((tab) => !currentKeys.has(tabKey(tab))),
  };
}

/**
 * Whether the live strip may be written under `key`.
 *
 * The empty key is not a bucket. A reset or an in-flight partition apply must
 * not record the strip it is in the middle of throwing away or rebuilding.
 * Before the first apply finishes, the strip on screen is the layout restore
 * (instance tabs already removed) and must not overwrite the stored list.
 */
export function shouldSaveEditorPartition(input: {
  key: string;
  suppress: boolean;
  applying: boolean;
  ready: boolean;
}): boolean {
  return input.ready && input.key !== '' && !input.suppress && !input.applying;
}

/**
 * Whether applying a partition strip should open the startup rail view.
 *
 * The bucket restores document tabs only. A session that already chose a rail
 * item keeps it. A fresh session (nothing chosen yet) takes
 * `workbench.startupEditorView`. A saved `activeViewId` is not an input: older
 * buckets stored the rail singleton there, and applying it reopened that
 * launcher on every start.
 */
export function railViewAfterPartitionApply(
  currentActiveViewId: string | null,
): 'keep' | 'startup' {
  return currentActiveViewId === null ? 'startup' : 'keep';
}

/**
 * Read the editor strip into a partition snapshot. Titles are not kept.
 * The rail singleton is not part of the bucket, so `activeViewId` is always
 * null. `focusedInstanceId` is which document tab is in front, or null when
 * the anchor is in front.
 */
export function captureEditorPartition(
  tabs: readonly {
    viewId: string;
    instanceKey?: string;
    active: boolean;
  }[],
): EditorPartitionSnapshot {
  const instances: EditorPartitionTab[] = [];
  let focusedInstanceId: string | null = null;
  for (const tab of tabs) {
    if (!tab.instanceKey) {
      continue;
    }
    instances.push({ viewId: tab.viewId, instanceKey: tab.instanceKey });
    if (tab.active) {
      focusedInstanceId = surfaceInstanceId(tab.viewId, tab.instanceKey);
    }
  }
  return { activeViewId: null, tabs: instances, focusedInstanceId };
}
